import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Account,
  ActivityFilterModel,
  ActivityOrderBy,
  cacheKeys, DayIntervalUnit,
  Issue,
  IssueMessageType,
  IssueType,
  Media,
  replaceMentionPlaceholderWithNames,
  Severity,
  useCreateMutation,
  useTogglePin,
  useTranslatedText,
  useUpdateIssue
} from '@gripp/shared-logic';
import { Pill, QrIcon } from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes';
import { StatusTag } from '../crop-circle/Components/Tag';
import { severityToVariant, TypeIcon, variantToSeverity } from '../crop-circle/Components/Type';
import { typeColors } from '../crop-circle/Components/Type/types';
import { Icon } from '../crop-circle/icons';
import { getIssueAuditData, getIssueFullName } from '../issue/issueFormatUtils';
import { IssueReminderModal } from '../issue/issueReminderModal';
import { useTypedMediaItem } from '../media/useTypedMediaItem';
import { IssueCardOld } from './issueCard-old';

export interface IssueCardData {
  type: string;
  asset: {
    name: string;
    coverImage?: {
      id?: string;
      url?: string;
      extension?: string;
      status?: string;
      type?: string;
    };
  };
  workspace: {
    id: string;
    name: string;
  };
  id: string;
  title: string;
  subtitle?: string;
  severity: Severity;
  createdAt: string;
  effectiveCreationDate?: string;
  isPinned?: boolean;
  updatedAt: string;
  labels?: {
    id: string;
    name: string;
  }[];
  nextReminderDate?: string;
  userMentions?: Account[];
  latestMessage: {
    text: string;
    createdBy: Account;
    userMentions: Account[];
    type: IssueMessageType;
  };
}

export type IssueCardProps = {
  account: Account | null;
  activity: IssueCardData;
  isCropCircleRedesignEnabled: boolean;
  showAssetName?: boolean;
  isUnread?: boolean;
  isMentionedInInbox?: boolean;
  filter: ActivityFilterModel;
  borderRadius?: number;
  isSelected?: boolean;
};

const SWIPEABLE_BUTTON_WIDTH = 150;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

// Right side underlay - shows issue type (revealed when swiping LEFT)
const IssueTypeUnderlay = ({ item }: { item: IssueCardData; }) => {
  const { t } = useTranslation();
  const { mutate: updateIssue } = useUpdateIssue();

  const variant = 'solved';
  const translationKey = `issue.types.${variant}`;
  const label = t(translationKey);

  const onIssueHeaderChanged = () => {
    const severity = variantToSeverity[variant];
    
    const input: any = {
      id: item.id,
      severity: severity,
    };

    if (
      item.type === 'NOTE' &&
      item.severity === 'purple' &&
      ['red', 'yellow', 'green'].includes(severity)
    ) {
      input.type = 'REPORT';
    }

    updateIssue({ input: input });
  };
  
  return (
    <TouchableOpacity
      style={[
        styles.underlayRight,
        {
          width: SWIPEABLE_BUTTON_WIDTH,
          backgroundColor: typeColors[variant],
          alignItems: 'center',
          justifyContent: 'center',
        },
      ]}
      onPress={() => onIssueHeaderChanged()}
    >
      <TypeIcon variant={variant} size={16} color="white" />
      <Text style={styles.underlayText}>
        {label}
      </Text>
    </TouchableOpacity>
  );
};

// Right side underlay - reminder and pin/unpin action buttons
const ActionButtonsUnderlay = ({ item, close }: { item: IssueCardData; close?: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { togglePin } = useTogglePin();
  const [isReminderModalVisible, setIsReminderModalVisible] = useState(false);

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  // Check if reminder is available (no existing reminder AND issue type is not REMINDER)
  const hasReminder = !!item?.nextReminderDate;
  const isReminderType = item?.type === IssueType.Reminder;
  const isReminderAvailable = !hasReminder && !isReminderType;

  const defaultValues: any = {
    alertType: '',
    inputField: '',
    days: 1,
    dayInterval: 1,
    dayIntervalUnit: DayIntervalUnit.Days,
    daysStartDate: moment().format('MM/DD/YYYY'),
    onDate: undefined,
    notes: '',
  };

  const submitReminder = async (input: any) => {
    // Ensure workspace.id is included in the input (required for Global Admins)
    const workflowInput: any = {
      ...input,
      workspace: {
        id: item.workspace?.id,
      },
    };

    await createWorkflow(
      { input: workflowInput },
      {
        onSuccess: async () => {
          setIsReminderModalVisible(false);
          // Refresh the workflow cache to show the new reminder
          queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
          // Close the swipe after successful reminder creation
          close?.();
        },
      }
    );
  };

  const handleReminder = () => {
    setIsReminderModalVisible(true);
  };

  const handlePinUnpin = async () => {
    try {
      // Toggle pin state
      const newPinState = !item.isPinned;
      
      // Optimistically update the cache immediately
      queryClient.setQueryData(
        cacheKeys('issue').all,
        (oldData: any) => {
          if (!oldData) return oldData;
          
          // Update the specific issue in all cached queries
          const updateIssueInData = (data: any) => {
            if (data?.issue?.findIssues?.items) {
              return {
                ...data,
                issue: {
                  ...data.issue,
                  findIssues: {
                    ...data.issue.findIssues,
                    items: data.issue.findIssues.items.map((issue: any) =>
                      issue.id === item.id
                        ? { ...issue, isPinned: newPinState }
                        : issue
                    ),
                  },
                },
              };
            }
            return data;
          };
          
          // Handle different cache structures
          if (Array.isArray(oldData)) {
            return oldData.map(updateIssueInData);
          }
          return updateIssueInData(oldData);
        }
      );
      
      // Perform the mutation
      await togglePin(item.id, newPinState);
      
      // Invalidate and refetch to ensure consistency with server
      await queryClient.invalidateQueries({ 
        queryKey: cacheKeys('issue').all,
        exact: false 
      });
      
      // Close the swipe after pin/unpin action completes
      close?.();
    } catch (error) {
      // If pin/unpin fails, revert optimistic update
      await queryClient.invalidateQueries({ 
        queryKey: cacheKeys('issue').all,
        exact: false 
      });
      // Still close the swipe
      close?.();
    }
  };

  const onReminderSuccess = () => {
    queryClient.invalidateQueries({ queryKey: cacheKeys('workflow').all });
  };

  // Convert item to Issue type for IssueReminderModal
  const issue: Issue = {
    id: item.id,
    workspace: item.workspace,
    type: item.type as IssueType,
    severity: item.severity,
    title: item.title,
    asset: item.asset,
  } as Issue;
  
  return (
    <>
      <View style={styles.underlayRight}>
        <TouchableOpacity
          style={[
            styles.underlayRightSquare1,
            {
              right: isReminderAvailable ? SWIPEABLE_BUTTON_WIDTH : 0,
              width: SWIPEABLE_BUTTON_WIDTH,
            },
            styles.underlayActionButton,
          ]}
          onPress={handlePinUnpin}
        >
          <View style={styles.underlayIconContainer}>
            <MaterialCommunityIcons
              name={item.isPinned ? 'pin-off' : 'pin'}
              color="white"
              size={24}
            />
            <Text style={styles.underlayText}>
              {item.isPinned ? t('issue.menu.unpinToAsset') : t('issue.menu.pinToAsset')}
            </Text>
          </View>
        </TouchableOpacity>
        {isReminderAvailable && (
          <TouchableOpacity
            style={[
              styles.underlayRightSquare2,
              {
                width: SWIPEABLE_BUTTON_WIDTH,
              },
              styles.underlayActionButton,
            ]}
            onPress={handleReminder}
          >
            <View style={styles.underlayIconContainer}>
              <MaterialCommunityIcons name="alarm" color="white" size={24} />
              <Text style={styles.underlayText}>{t('issue.menu.reminder')}</Text>
            </View>
          </TouchableOpacity>
        )}
      </View>
      <IssueReminderModal
        defaultValues={defaultValues}
        issue={issue}
        visible={isReminderModalVisible}
        hide={() => {
          setIsReminderModalVisible(false);
          // Close the swipe when modal is dismissed
          close?.();
        }}
        submitReminder={submitReminder}
        onSuccess={onReminderSuccess}
      />
    </>
  );
};

const useIssueCardViewModel = (props: IssueCardProps) => {
  const { t } = useTranslation();
  const { activity } = props;
  const [auditData, setAuditData] = useState({ who: '...', when: '...' });
  const [isHovered, setIsHovered] = useState(false);

  const coverImageCandidate = activity.asset?.coverImage as unknown;
  const coverImageForTypedMedia =
    isMediaLike(coverImageCandidate) ? (coverImageCandidate as Media) : undefined;

  const { typedMediaItem: typedCoverImage } =
    useTypedMediaItem(coverImageForTypedMedia);

  useEffect(() => {
    setAuditData(
      getIssueAuditData(
        activity as unknown as Issue,
        props.filter.orderBy as ActivityOrderBy
      )
    );
  }, [activity, props.filter.orderBy]);

  const getProcessedTitle = () => {
    const titleText = activity.title;

    if (titleText.includes('@[') && (activity as any).userMentions) {
      return replaceMentionPlaceholderWithNames(
        titleText,
        (activity as any).userMentions,
        t('mentions.unknownUser'),
        true
      );
    }

    return titleText;
  };

  const getLatestMessageText = () => {
    const latestMessage = activity?.latestMessage;

    if (!latestMessage) {
      return '';
    }

    const user = getIssueFullName(
      latestMessage.createdBy?.id,
      latestMessage.createdBy?.name
    );

    if (props.isUnread && props.isMentionedInInbox) {
      return (
        <Text style={{ color: Colors.primary, fontSize: 14 }}>
          {t('activity.list.youAreMentioned')}
        </Text>
      );
    }

    if (
      latestMessage.type === IssueMessageType.RoutineMessage ||
      latestMessage.type === IssueMessageType.RoutineExceptionMessage
    ) {
      return activity?.subtitle
        ? `${user} ${activity.subtitle}`
        : t('routines.message.messageTitle', { name: user }).slice(0, -1);
    }

    if (
      latestMessage.type === IssueMessageType.SystemMessage ||
      latestMessage.type === IssueMessageType.RoutinePromptMessage
    ) {
      const { translatedText } = useTranslatedText('text', latestMessage);
      return translatedText;
    }

    if (!latestMessage.text) {
      return `${user}: ${t('issue.latestMessage.media')}`;
    }

    const { translatedText } = useTranslatedText('text', latestMessage);
    const messageText = translatedText || latestMessage?.text;

    const processedText = messageText.includes('@[')
      ? replaceMentionPlaceholderWithNames(
          messageText,
          latestMessage?.userMentions,
          t('mentions.unknownUser'),
          true
        )
      : messageText;

    return `${user}: ${processedText}`;
  };

  const latestMessageContent = getLatestMessageText();

  const renderLatestMessage = (textStyle: any, numberOfLines = 1) => {
    if (typeof latestMessageContent === 'string') {
      return (
        <Text variant="bodyMedium" style={textStyle} numberOfLines={numberOfLines}>
          {latestMessageContent}
        </Text>
      );
    }

    return latestMessageContent;
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }
      : {};

  return {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  };
};

function isMediaLike(value: unknown): value is Media {
  const v = value as any;
  return (
    !!v &&
    typeof v === 'object' &&
    typeof v.id === 'string' &&
    typeof v.extension === 'string' &&
    typeof v.type === 'string' &&
    typeof v.status === 'string'
  );
}

export const IssueCardBase = (props: IssueCardProps) => {
  const {
    activity,
    auditData,
    isHovered,
    hoverProps,
    typedCoverImage,
    getProcessedTitle,
    renderLatestMessage,
  } = useIssueCardViewModel(props);

  const severityVariant = severityToVariant[activity.severity] || 'attention';

  const coverImage = typedCoverImage || activity.asset?.coverImage;
  const coverUri =
    coverImage?.url && `${coverImage.url}`.trim().length > 0 ? coverImage.url : undefined;

  return (
    <View
      style={[
        styles.card,
        styles.cropCard,
        props.isSelected && styles.cardSelected,
        isHovered && styles.cropCardHovered,
        props.borderRadius !== undefined && { borderRadius: props.borderRadius },
      ]}
      {...hoverProps}
    >
      <View style={styles.cropBodyRow}>
        <View style={styles.cropImageWrapper}>
          {coverUri ? (
            <Image source={{ uri: coverUri }} style={styles.cropImage} />
          ) : (
            <View style={styles.cropImagePlaceholder}>
              <Icon name="QR" size={20} color={Colors.grayText} />
            </View>
          )}
        </View>

        <View style={styles.cropContent}>
          <View style={styles.cropWorkspaceRow}>
            <Text style={styles.cropWorkspace} numberOfLines={1} ellipsizeMode="tail">{activity.workspace.name}</Text>
          </View>

          <View style={styles.cropHeader}>
            <View style={styles.assetNamePillWrapper}>
              <Pill
                label={activity.asset?.name || activity.workspace.name}
                icon={QrIcon}
                testID="issue-asset-name-label"
                disabled={true}
              />
            </View>

            <View style={styles.cropMetaRight}>
              <Text variant="bodyMedium" style={styles.cropWhenText}>
                {auditData.when}
              </Text>
              {props.isUnread && <Icon name="Dot" size={12} color={Colors.primary} />}
            </View>
          </View>

          <View style={styles.cropTitleRow}>
            <View style={styles.cropLeadingIcons}>
              <TypeIcon variant={severityVariant} size={16} />
              {activity.nextReminderDate && (
                <View style={styles.cropReminderBadge}>
                  <Icon name="Clock B" size={12} color={Colors.grayText} />
                </View>
              )}
            </View>

            <Text style={styles.cropTitle} numberOfLines={1}>
              {getProcessedTitle()}
            </Text>

            {activity.isPinned && <Icon name="Pin" size={16} color={Colors.grayText} />}
          </View>

          <View style={styles.cropMessageRow}>
            {renderLatestMessage(styles.cropLastMessage, 2)}
          </View>

          {activity.labels && activity.labels.length > 0 && (
            <View style={styles.cropLabelsContainer}>
              {activity.labels.map((label) => (
                <StatusTag key={label.id} variant="neutral" text={label.name} />
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export const IssueCard = (props: IssueCardProps) => {
  // Hooks must be called unconditionally at the top
  const cardRef = useRef<View>(null);
  const swipeableRef = useRef<any>(null);
  const [isSwipeOpen, setIsSwipeOpen] = useState<boolean>(false);

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleRedesignEnabled = props.isCropCircleRedesignEnabled || false;

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return <IssueCardOld {...props} />;
  }

  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const cardContent = <IssueCardBase {...props} borderRadius={0} />;

  // Only enable swipeable on mobile platforms
  if (Platform.OS === 'web') {
    return <IssueCardBase {...props} />;
  }

  // Dynamically import SwipeableItem only on mobile to avoid web bundle bloat
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const SwipeableItem = require('react-native-swipeable-item').default;

  if (!SwipeableItem) {
    return <IssueCardBase {...props} />;
  }

  // Calculate if reminder is available for dynamic snap points
  const hasReminder = !!props.activity?.nextReminderDate;
  const isReminderType = props.activity?.type === IssueType.Reminder;
  const isReminderAvailable = !hasReminder && !isReminderType;

  // Only allow left swipe for purple, red or yellow severity
  const enableRightSwipe = (
    props.activity?.severity === 'purple' ||
    props.activity?.severity === 'red' || 
    props.activity?.severity === 'yellow'
  );

  // Snap points: SWIPEABLE_BUTTON_WIDTH * 2 for both buttons (reminder + pin), SWIPEABLE_BUTTON_WIDTH for single button (pin only)
  const snapPointsLeft = isReminderAvailable ? [SWIPEABLE_BUTTON_WIDTH * 2] : [SWIPEABLE_BUTTON_WIDTH];
  const snapPointsRight = enableRightSwipe ? [SWIPEABLE_BUTTON_WIDTH] : [];

  return (
    <SwipeableItem
      ref={swipeableRef}
      // track open state so we can decide whether to allow touches to continue
      onChange={(open: boolean) => setIsSwipeOpen(open)}
      item={props.activity}
      renderUnderlayLeft={(params: any) => {
        return <ActionButtonsUnderlay item={props.activity} close={params.close} />;
      }}
      renderUnderlayRight={enableRightSwipe ? (() => {
        return <IssueTypeUnderlay item={props.activity} />;
      }) : undefined}
      snapPointsLeft={snapPointsLeft}
      snapPointsRight={snapPointsRight}
      overSwipe={0}
      key={props.activity.id}
    >
      <View
        ref={cardRef}
        style={{ borderRadius: 0, overflow: 'visible' }}
        // If a swipe underlay is open, close it and consume the touch.
        // Only allow the touch to continue if the swipeable is currently closed.
        onStartShouldSetResponder={() => {
          if (isSwipeOpen) {
            swipeableRef.current?.close?.();
            setIsSwipeOpen(false);
            return true; // consume the touch so the content doesn't react
          }

          return false; // allow the touch to continue to children
        }}
      >
        {cardContent}
      </View>
    </SwipeableItem>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    alignSelf: 'stretch',
    height: 'auto',
    padding: 16,
    ...Platform.select({
      web: { gap: 12 },
      default: { gap: 10 },
    }),
    },
    cropCard: {
    backgroundColor: Colors.white,
    paddingVertical: 18,
    paddingHorizontal: 16,
    gap: 12,
    shadowColor: Colors.black,
    shadowOpacity: 0.04,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    ...Platform.select({
      android: {
        elevation: 2,
      },
    }),
  },
  underlayLeft: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  underlayRight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  underlayRightSquare1: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    // width is set dynamically based on cardHeight
    backgroundColor: '#FFB368', // orange
  },
  underlayRightSquare2: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    // right and width are set dynamically based on cardHeight
    backgroundColor: '#2CA9E0', // cyan
  },
  underlayIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  underlayText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  underlayActionButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  cropCardHovered: {
    shadowOpacity: 0.08,
    backgroundColor: Colors.primaryGrayBackground,
    cursor: 'pointer',
    ...Platform.select({
      android: {
        elevation: 4,
      },
    }),
  },
  cardSelected: {
    backgroundColor: Colors.secondaryGrayHeader,
    borderRadius: 0,
  },
  cropBodyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  cropImageWrapper: {
    width: 72,
    height: 72,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: Colors.primaryGrayBackground,
  },
  cropImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  cropImagePlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cropContent: {
    flex: 1,
    gap: 8,
  },
  cropWorkspaceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  cropWorkspace: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  assetNamePillWrapper: {
    marginHorizontal: -8,
    flexShrink: 1,
    flexBasis: 'auto',
    maxWidth: '70%', // Safe constraint to prevent date overlap
  },
  cropMetaRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cropWhenText: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.grayText,
  },
  cropTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cropLeadingIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  cropReminderBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cropTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.black,
  },
  cropMessageRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cropLastMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
    lineHeight: 20,
    flex: 1,
  },
  cropLabelsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
});
